import { core } from '@icc/common/helpers';
import { Injectable } from '@angular/core';
import { ConfiguratorsDataService } from '@icc/common/configurators/configurators-data.service';
import { EventBusService } from '@icc/common/event-bus.service';
import { isArray, isObject } from '@icc/helpers';
import { WindowActiveConfiguration } from '../configurations/WindowActiveConfiguration';
import { IccColor } from '../data-types/Color';

/**
 * Usługa mapowania kolorów.
 *
 * @export
 * @class ColorMappingService
 */
@Injectable()
export class ColorMappingService {
    /**
     * Mapowanie kolorów
     *
     *
     * @memberOf ColorMappingService
     */
    colorMaps: any[] = [];

    /**
     * Czy załadowne dane.
     *
     *
     * @memberOf ColorMappingService
     */
    loadedData = false;

    /**
     * Creates an instance of ColorMappingService.
     *
     * @param {any} $rootScope
     * @param {any} configuratorsDataService
     *
     * @memberOf ColorMappingService
     */
    constructor(
        private configuratorsDataService: ConfiguratorsDataService,
        private eventBusService: EventBusService
    ) {
        'ngInject';

        if (configuratorsDataService.loaded) {
            this.init();
        }

        this.eventBusService.subscribeWithoutConfiguration('loadedConfiguratorsData', () =>
            this.init()
        );
    }

    /**
     * Inicjalizuje dane.
     *
     *
     * @memberOf ColorMappingService
     */
    init() {
        this.colorMaps = this.configuratorsDataService.data.colorMaps || [];
        this.loadedData = true;
    }

    /**
     * Zamienia typ na model Cake
     *
     * @param {string} type Typ
     * @returns {string} Model Cake
     *
     * @memberOf ColorMappingService
     */
    convertTypeToModel(type) {
        let model = 'Window.WindowColor';
        switch (type) {
            case 'window':
                model = 'Window.WindowColor';
                break;
            case 'ral':
                model = 'Window.WindowColor';
                break;
            case 'roller':
                model = 'RollerShutter.RollerShutterColor';
                break;
            case 'accessory':
                model = 'Window.WindowHandlesColor';
                break;
            default:
                model = 'Window.WindowColor';
        }
        return model;
    }

    /**
     * Zwraca kolory pasujące do przekazanego koloru.
     *
     * @param {any} sourceColorId   Id źródłowego koloru.
     * @param {any} sourceColorType Rodzaj źródłowego koloru.
     * @param {any} targetColorType Rodzaj zwracanego koloru.
     * @return {any[]} Kolory pasujące do przekazanego koloru.
     *
     * @memberOf ColorMappingService
     */
    getColors(sourceColorId: number | string | null, sourceColorType: string, targetColorType: string): number[] {
        const sourceColorModel = this.convertTypeToModel(sourceColorType);
        const targetColorModel = this.convertTypeToModel(targetColorType);
        const matchedColorMaps = this.colorMaps.filter(
            c => Number(c.source_id) === Number(sourceColorId) && c.source_model === sourceColorModel
        );
        const defaultMapping: number[] = sourceColorId && sourceColorType === targetColorType ? [Number(sourceColorId)] : [];
        if (matchedColorMaps.length > 0) {
            const mapping = core.parseJson(matchedColorMaps[0].target) || {};
            if (mapping && isArray(mapping[targetColorModel])) {
                return defaultMapping.concat(mapping[targetColorModel].map(Number));
            }
        }
        return defaultMapping;
    }

    /**
     * Zwrca id koloru okna.
     *
     * @param {any} conf Konfiguracja.
     * @param {string} [side='outer'] Strona okna.
     * @param {string} [field='id'] Nazwa pola.
     * @returns Wartość pola koloru.
     *
     * @memberOf ColorMappingService
     */
    getWindowColorId(conf: WindowActiveConfiguration, side: 'outer' | 'inner' = 'outer', field: keyof Partial<IccColor> = 'id') {
        if (
            ['window', 'hs', 'door', 'folding_door', 'sliding_door'].indexOf(conf.type) === -1
            || !isObject(conf.Colors)
            || !isObject(conf.Colors.frame)
        ) {
            return null;
        }
        if (side === 'inner') {
            if (conf.Colors.frame.inner && conf.Colors.frame.inner[field] != null) {
                return Number(conf.Colors.frame.inner[field]);
            } else if (conf.Colors.frame.core && conf.Colors.frame.core[field] != null) {
                return Number(conf.Colors.frame.core[field]);
            }
        } else {
            if (conf.Colors.frame.alushell && conf.Colors.frame.alushell[field] != null) {
                return Number(conf.Colors.frame.alushell[field]);
            } else if (conf.Colors.frame.outer && conf.Colors.frame.outer[field] != null) {
                return Number(conf.Colors.frame.outer[field]);
            } else if (conf.Colors.frame.core && conf.Colors.frame.core[field] != null) {
                return Number(conf.Colors.frame.core[field]);
            }
        }
        return null;
    }
}
